/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useLocation } from 'js/lib/useRouter';

import { Typography2 } from '@coursera/cds-core';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import SSOEnforcementBanner from 'bundles/authentication-common/components/SSOEnforcementBanner';
import {
  encodeSsoSamlMagicLink,
  getMagicLinkType,
  getThirdPartyOrganizationSsoLoginUrl,
} from 'bundles/authentication-common/components/actions';
import type { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import { AUTHENTICATION_MODAL_MODES, SSO_BANNER_NAMES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import useDisableDomainValidationEmailPrefill from 'bundles/authentication-common/hooks/useDisableDomainValidationEmailPrefill';
import { SSOTypes } from 'bundles/third-party-auth/lib';

import _t from 'i18n!nls/authentication';

type InvalidDomainSSOEnforcementBannerProps = {
  ssoError: InvalidDomainSSOError;
  navigate?: (
    path: AUTHENTICATION_MODAL_MODES_KEY,
    options?: $TSFixMe,
    shouldSwitchFormsByQueryParam?: boolean
  ) => void;
  css?: Interpolation<Theme>;
};

const InvalidDomainSSOEnforcementBanner = ({
  ssoError,
  navigate,
  css: customCss,
}: InvalidDomainSSOEnforcementBannerProps) => {
  const [isResending, setIsResending] = React.useState(false);
  const redirectUrlRef = React.useRef<string | undefined>(undefined);
  const location = useLocation();
  const returnTo = (location.query ?? {}).redirectTo;
  const isLtiDomainFailure = ssoError.method?.toLowerCase() === SSOTypes.LTI;
  const getDisableDomainValidationEmailPrefill = useDisableDomainValidationEmailPrefill();
  const [disableDomainValidationEmailPrefill, setDisableDomainValidationEmailPrefill] = React.useState(false);

  React.useEffect(() => {
    const organizationId = ssoError.ssoLoginResponse?.thirdPartyOrganizationId;
    redirectUrlRef.current = undefined;

    (async () => {
      const shouldDisablePrefill = await getDisableDomainValidationEmailPrefill(organizationId);
      setDisableDomainValidationEmailPrefill(shouldDisablePrefill);

      if (organizationId) {
        try {
          redirectUrlRef.current = await getThirdPartyOrganizationSsoLoginUrl(organizationId, returnTo || '/');
        } catch {
          redirectUrlRef.current = undefined;
        }
      }
    })();
  }, [ssoError.ssoLoginResponse?.thirdPartyOrganizationId, returnTo, getDisableDomainValidationEmailPrefill]);

  const resendMagicLink = async () => {
    const ssoErrorTokenExists = !!ssoError?.token;
    if (ssoErrorTokenExists && !isResending) {
      setIsResending(true);
      try {
        const result = await encodeSsoSamlMagicLink({
          samlLoginId: ssoError?.token || '',
          magicLinkType: getMagicLinkType(ssoError.ssoLoginResponse?.action),
        });
        if (result.success) {
          navigate?.(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email: ssoError.ssoLoginResponse?.email });
        } else if (result.shouldRedirectToIdP && redirectUrlRef.current) {
          window.location.replace(redirectUrlRef.current);
        }
      } finally {
        setIsResending(false);
      }
    }
  };

  if (disableDomainValidationEmailPrefill) {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {_t('One more step to get you in')}
          </Typography2>
          <br />
          {_t("SSO didn't work for this email. Please continue with email and password or use a social account below.")}
        </>
      </SSOEnforcementBanner>
    );
  } else if (ssoError.showResendLink && ssoError.ssoLoginResponse?.action === 'VERIFY_ACCOUNT_WITH_MAGIC_LINK') {
    return (
      <SSOEnforcementBanner
        severity="warning"
        title={_t('Verify your account to continue')}
        bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN}
        css={customCss}
      >
        <FormattedMessage
          message={_t(
            "Your organization's sign-in details have changed. We've sent a verification link to your SSO-linked account. Didn't get it? {resendLink}"
          )}
          resendLink={
            <LinkButton disabled={isResending} onClick={resendMagicLink}>
              {_t('Resend')}
            </LinkButton>
          }
        />
      </SSOEnforcementBanner>
    );
  } else if (ssoError.showResendLink) {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {_t('A sign-in link is on its way')}
          </Typography2>
          <br />
          <FormattedMessage
            message={_t(
              "SSO didn't work for this email, so we've sent you a secure link instead. Check your inbox to continue. Didn’t get? {resendLink}. If you still have trouble, contact your admin."
            )}
            resendLink={
              <LinkButton disabled={isResending} onClick={resendMagicLink}>
                {_t('Resend link')}
              </LinkButton>
            }
          />
        </>
      </SSOEnforcementBanner>
    );
  } else if (ssoError.isSSOLockedOut) {
    return (
      <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        {_t(
          "You can't sign in. Domain verification failed. Your organization requires mandatory SSO. Contact your Admin for access."
        )}
      </SSOEnforcementBanner>
    );
  } else if (ssoError.ssoLoginResponse?.authMode === 'login') {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {isLtiDomainFailure
              ? _t("SSO isn't available because this email domain isn't verified.")
              : _t('One more step to get you in')}
          </Typography2>
          <br />
          {isLtiDomainFailure
            ? _t(
                'You can still use another log-in method. For LMS grade sync, use a Coursera password with the same email you launched from your LMS.'
              )
            : _t("SSO didn't work for this email. Login in with a link, your password, or a social account below.")}
        </>
      </SSOEnforcementBanner>
    );
  } else if (ssoError.ssoLoginResponse?.authMode === 'signup') {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {isLtiDomainFailure
              ? _t("SSO isn't available because this email domain isn't verified.")
              : _t('One more step to get you in')}
          </Typography2>
          <br />
          {isLtiDomainFailure
            ? _t(
                'You can still use another sign-up method. For LMS grade sync, set a Coursera password with the same email you launched from your LMS.'
              )
            : _t("SSO didn't work for this email. Set a password or continue with a social account below.")}
        </>
      </SSOEnforcementBanner>
    );
  } else {
    return (
      <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        {_t(
          "We couldn't verify your email domain. Login or create an account using another login method. Contact your admin if the issue persists."
        )}
      </SSOEnforcementBanner>
    );
  }
};

export default InvalidDomainSSOEnforcementBanner;
